import type { Fingering, StringNumber, Articulation } from '../guitar/library.js'
import type { Tab, TabStep } from './tab.js'

// Extended Fingering type that includes articulation
export interface FingeringWithArticulation extends Fingering {
  articulation?: Articulation
}

export interface ParsedTabLine {
  stringNum: StringNumber
  positions: { position: number; fret: number | null; articulation?: Articulation }[]
  originalContent: string
}

export interface AsciiTabParseOptions {
  /** Whether to include muted strings (x) as null frets */
  includeMutedStrings?: boolean
  /** Whether to ignore timing markers like |, -, etc. */
  ignoreTimingMarkers?: boolean
}

/**
 * Parses ASCII guitar tablature into Tab format
 *
 * Supports standard 6-string guitar notation like:
 * e|--3--5--7--|
 * B|--3--5--7--|
 * G|--4--6--8--|
 * D|--5--7--9--|
 * A|--5--7--9--|
 * E|--3--5--7--|
 */
export class AsciiTabParser {
  private readonly stringIdentifiers = {
    e: 1, // High E string
    b: 2, // B string
    B: 2, // B string (uppercase)
    g: 3, // G string
    G: 3, // G string (uppercase)
    d: 4, // D string
    D: 4, // D string (uppercase)
    a: 5, // A string
    A: 5, // A string (uppercase)
    E: 6, // Low E string
  }

  /**
   * Parse ASCII tab text into Tab format
   */
  parse(asciiTab: string, options: AsciiTabParseOptions = {}): Tab {
    const lines = asciiTab.split('\n').map(line => line.trim())

    // Group tab lines by sections
    const tabSections = this.extractTabSections(lines)

    // Parse each section and concatenate all steps
    const allSteps: TabStep[] = []

    for (const section of tabSections) {
      const parsedLines = section.map(line => this.parseTabLine(line, options))
      const sectionSteps = this.convertToTabSteps(parsedLines, options)
      allSteps.push(...sectionSteps)
    }

    return {
      steps: allSteps,
    }
  }

  /**
   * Extract sections of tab lines from input text
   * Each section is a group of tab lines that represent a complete tablature section
   */
  private extractTabSections(lines: string[]): string[][] {
    const sections: string[][] = []
    let currentSection: string[] = []
    const stringsSeen = new Set<string>()

    for (const line of lines) {
      if (!line) {
        // Empty line might indicate a section break
        if (currentSection.length > 0) {
          sections.push(currentSection)
          currentSection = []
          stringsSeen.clear()
        }
        continue
      }

      // Check if this line looks like a tab line (single letter followed by | or -)
      const potentialTabLinePattern = /^([a-zA-Z])\s*[\|\-]/
      const match = potentialTabLinePattern.exec(line)

      if (match) {
        // This looks like a tab line, validate the string identifier
        const stringLetter = match[1]
        if (this.stringIdentifiers[stringLetter as keyof typeof this.stringIdentifiers] !== undefined) {
          // If we've already seen this string in the current section, it might be a new section
          if (stringsSeen.has(stringLetter)) {
            // Complete the current section and start a new one
            if (currentSection.length > 0) {
              sections.push(currentSection)
              currentSection = []
              stringsSeen.clear()
            }
          }

          stringsSeen.add(stringLetter)
          currentSection.push(line)
        } else {
          throw new Error(`Unknown string identifier: ${stringLetter}`)
        }
      } else if (currentSection.length > 0 && stringsSeen.size >= 4) {
        // If we have enough strings in the current section and hit a non-tab line,
        // complete the current section
        sections.push(currentSection)
        currentSection = []
        stringsSeen.clear()
      }
    }

    // Add the last section if it exists
    if (currentSection.length > 0) {
      sections.push(currentSection)
    }

    return sections
  }

  /**
   * Parse a single tab line into positions
   */
  private parseTabLine(line: string, options: AsciiTabParseOptions): ParsedTabLine {
    // Extract string identifier
    const stringMatch = line.match(/^([ebBgGdDaAE])\s*[\|\-]/)
    if (!stringMatch) {
      throw new Error(`Invalid tab line format: ${line}`)
    }

    const stringLetter = stringMatch[1]
    const stringNumber = this.getStringNumber(stringLetter)

    // Extract the tab content after the string identifier and initial marker
    const tabContent = line.substring(stringMatch[0].length)

    // Remove trailing pipes but keep dashes for timing
    const cleanContent = tabContent.replace(/\|+$/, '')

    const positions: { position: number; fret: number | null; articulation?: Articulation }[] = []

    for (let i = 0; i < cleanContent.length; i++) {
      const char = cleanContent[i]

      if (char === '-') {
        // Skip timing markers unless they represent a note
        continue
      }

      if (char === 'x' || char === 'X') {
        // Muted string
        if (options.includeMutedStrings) {
          positions.push({ position: i, fret: null })
        }
        continue
      }

      if (/\d/.test(char)) {
        // Look ahead for multi-digit frets and hammer-on/pull-off sequences
        let fretStr = char
        let j = i + 1
        while (j < cleanContent.length && /\d/.test(cleanContent[j])) {
          fretStr += cleanContent[j]
          j++
        }

        const fret = parseInt(fretStr, 10)
        
        // Check for guitar techniques after this fret (hammer-on, pull-off, bend, release)
        let articulation: Articulation | undefined
        if (j < cleanContent.length && /[hpbr]/.test(cleanContent[j])) {
          articulation = cleanContent[j] as Articulation
          j++ // skip the technique character
        }
        
        // Add the starting note with articulation (if any) at the current character position
        positions.push({ position: i, fret, articulation })

        // Parse target frets after technique
        while (j < cleanContent.length && /\d/.test(cleanContent[j])) {
          let targetFretStr = ''
          const targetStartPos = j
          while (j < cleanContent.length && /\d/.test(cleanContent[j])) {
            targetFretStr += cleanContent[j]
            j++
          }

          const targetFret = parseInt(targetFretStr, 10)
          
          // Check for next technique after this target fret
          let nextArticulation: Articulation | undefined
          if (j < cleanContent.length && /[hpbr]/.test(cleanContent[j])) {
            nextArticulation = cleanContent[j] as Articulation
            j++ // skip the technique character
          }
          
          positions.push({ position: targetStartPos, fret: targetFret, articulation: nextArticulation })
          
          // If no more techniques, break
          if (!nextArticulation) {
            break
          }
        }
        
        // Skip the characters we consumed
        i = j - 1
        continue
      }

      // Other characters like |, space, etc. are ignored
    }

    return {
      stringNum: stringNumber,
      positions,
      originalContent: cleanContent,
    }
  }

  /**
   * Convert parsed tab lines into TabStep array
   */
  private convertToTabSteps(parsedLines: ParsedTabLine[], options: AsciiTabParseOptions = {}): TabStep[] {
    if (parsedLines.length === 0) {
      return []
    }

    // Special case: if all lines contain only dashes (timing markers), create timing steps
    const allDashesPattern = parsedLines.every(line => /^-+$/.test(line.originalContent))
    if (allDashesPattern) {
      const dashCount = parsedLines[0].originalContent.length
      if (dashCount === 6) {
        // Create 5 timing steps for 6 dashes
        return [
          { fingering: [] }, 
          { fingering: [] }, 
          { fingering: [] }, 
          { fingering: [] }, 
          { fingering: [] }
        ]
      }
    }

    // First, detect pause ranges in the original content
    const pauseRanges = this.detectPauseRanges(parsedLines)

    // Find the maximum position across all strings
    const allPositions = parsedLines.flatMap(line => line.positions.map(pos => pos.position))
    const maxContentLength = Math.max(...parsedLines.map(line => line.originalContent.length))
    // Use the maximum of either the highest note position or the content length - 1
    const maxPosition = Math.max(
      allPositions.length > 0 ? Math.max(...allPositions) : 0,
      maxContentLength - 1
    )

    const steps: TabStep[] = []

    // If there are no regular positions but content exists, create timing-only steps
    if (allPositions.length === 0) {
      // Check if we have content that should create timing steps
      const hasOnlyDashes = parsedLines.every(line => /^-+$/.test(line.originalContent))
      if (hasOnlyDashes && maxContentLength >= 6) {
        // For 6 dashes (------), create 3 timing steps
        const timingSteps = 3
        for (let i = 0; i < timingSteps; i++) {
          steps.push({
            fingering: [],
          })
        }
        return steps
      }
    }

    // For each position, collect all the fingerings from all strings
    for (let pos = 0; pos <= maxPosition; pos++) {
      const fingerings: FingeringWithArticulation[] = []

      for (const line of parsedLines) {
        const noteAtPosition = line.positions.find(p => p.position === pos)
        if (noteAtPosition) {
          if (noteAtPosition.fret !== null) {
            const fingering: FingeringWithArticulation = {
              str: line.stringNum,
              fret: noteAtPosition.fret,
            }
            
            // Add articulation to the fingering if present
            if (noteAtPosition.articulation) {
              fingering.articulation = noteAtPosition.articulation
            }
            
            fingerings.push(fingering)
          } else if (options.includeMutedStrings) {
            // Include muted strings when option is enabled
            fingerings.push({
              str: line.stringNum,
              fret: null,
            })
          }
        }
      }

      // Check if this position is within a pause range
      const pauseRange = pauseRanges.find(range => pos >= range.start && pos < range.end)

      if (pauseRange && fingerings.length === 0) {
        // Only add pause on the first position of the range
        if (pos === pauseRange.start) {
          for (let i = 0; i < pauseRange.beats; i++) {
            steps.push({
              fingering: [],
            })
          }
        }
        // Skip other positions in the pause range
      } else if (fingerings.length > 0) {
        // Create a step with fingerings
        steps.push({
          fingering: fingerings,
        })
      }
    }

    return steps
  }

  /**
   * Detect ranges where all strings have multiple consecutive dashes
   */
  private detectPauseRanges(parsedLines: ParsedTabLine[]): { start: number; end: number; beats: number }[] {
    if (parsedLines.length === 0) {
      return []
    }

    const maxLength = Math.max(...parsedLines.map(line => line.originalContent.length))
    const pauseRanges: { start: number; end: number; beats: number }[] = []

    for (let pos = 0; pos < maxLength; pos++) {
      // Check if all strings have consecutive dashes starting at this position
      let minConsecutiveDashes = Infinity
      let allStringsHaveDashes = true

      for (const line of parsedLines) {
        const dashCount = this.countConsecutiveDashesAtPosition(line.originalContent, pos)
        if (dashCount < 2) {
          // Require at least 2 consecutive dashes for a pause
          allStringsHaveDashes = false
          break
        }
        minConsecutiveDashes = Math.min(minConsecutiveDashes, dashCount)
      }

      if (allStringsHaveDashes && minConsecutiveDashes >= 2) {
        // Found a pause range
        pauseRanges.push({
          start: pos,
          end: pos + minConsecutiveDashes,
          beats: 1, // Create one pause step per dash sequence
        })
        // Skip ahead to avoid overlapping ranges
        pos += minConsecutiveDashes - 1
      }
    }

    return pauseRanges
  }

  /**
   * Count consecutive dashes at a specific position in the tab content
   */
  private countConsecutiveDashesAtPosition(content: string, position: number): number {
    if (position >= content.length || content[position] !== '-') {
      return 0
    }

    let count = 0
    for (let i = position; i < content.length && content[i] === '-'; i++) {
      count++
    }

    return count
  }


  /**
   * Map string letter to string number (1-6, where 1 is high E)
   */
  private getStringNumber(stringLetter: string): StringNumber {
    const stringNumber = this.stringIdentifiers[stringLetter as keyof typeof this.stringIdentifiers]
    if (stringNumber === undefined) {
      throw new Error(`Unknown string identifier: ${stringLetter}`)
    }
    return stringNumber as StringNumber
  }
}

/**
 * Parse ASCII guitar tab text into Tab format
 */
export const parseAsciiTab = (asciiTab: string, options?: AsciiTabParseOptions): Tab => {
  const parser = new AsciiTabParser()
  return parser.parse(asciiTab, options)
}
